<template>
  <div class="section country-index-notes">
    <div class="container is-fluid">
      <div class="page-header">
        <div>
          <h1>Country index notes</h1>
          <p class="has-text-grey">Click any note cell to add or edit notes. Changes save when you leave a cell.</p>
          <p v-if="lastSaved" class="has-text-grey">Last saved {{ lastSaved | moment('M/DD/YYYY h:mm A') }}</p>
        </div>
      </div>

      <b-notification v-if="error" type="is-danger" :closable="false">
        {{ error }}
        <b-button v-if="hasConflict" size="is-small" class="is-pulled-right" @click="getNotes">Reload notes</b-button>
      </b-notification>

      <div class="notes-toolbar">
        <div class="format-controls">
          <b-button size="is-small" title="Bold" @mousedown.prevent @click="formatCell('bold')" :disabled="!activeCell">
            <i class="fas fa-bold"></i>
          </b-button>
          <b-button size="is-small" title="Italic" @mousedown.prevent @click="formatCell('italic')" :disabled="!activeCell">
            <i class="fas fa-italic"></i>
          </b-button>
          <b-button size="is-small" title="Highlight" @mousedown.prevent @click="formatCell('hiliteColor', '#fff2a8')" :disabled="!activeCell">
            <i class="fas fa-highlighter"></i>
          </b-button>
          <b-button size="is-small" title="Clear formatting" @mousedown.prevent @click="formatCell('removeFormat')" :disabled="!activeCell">
            <i class="fas fa-eraser"></i>
          </b-button>
        </div>
        <div class="table-controls">
          <b-input v-model="filter" size="is-small" placeholder="Filter countries" class="country-filter"></b-input>
        </div>
      </div>

      <div class="notes-table-wrap">
        <b-loading :active="isLoading" :is-full-page="false"></b-loading>
        <table v-if="!isLoading" class="table is-bordered is-hoverable is-fullwidth notes-table">
          <thead>
            <tr>
              <th class="country-column">Country</th>
              <th>Watchlist status</th>
              <th>Other open issues</th>
              <th>Communication channel details</th>
              <th>Lawyer group details</th>
              <th class="date-column">Last counsel update</th>
              <th class="updated-column">Last updated as of</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in filteredNotes" :key="row.CountryId">
              <th class="country-column">{{ row.Country }}</th>
              <td :class="{ 'note-cell-has-value': fieldHasValue(row.WatchlistStatus) }">
                <div
                  class="editable-cell"
                  contenteditable="true"
                  dir="ltr"
                  data-placeholder="Add watchlist status"
                  v-html="row.WatchlistStatus"
                  @focus="setActiveCell(row, 'WatchlistStatus', $event)"
                  @input="markCellDirty($event)"
                  @blur="saveField(row, 'WatchlistStatus', $event)">
                </div>
              </td>
              <td :class="{ 'note-cell-has-value': fieldHasValue(row.OtherOpenIssues) }">
                <div
                  class="editable-cell"
                  contenteditable="true"
                  dir="ltr"
                  data-placeholder="Add open issues"
                  v-html="row.OtherOpenIssues"
                  @focus="setActiveCell(row, 'OtherOpenIssues', $event)"
                  @input="markCellDirty($event)"
                  @blur="saveField(row, 'OtherOpenIssues', $event)">
                </div>
              </td>
              <td :class="{ 'note-cell-has-value': fieldHasValue(row.CommunicationChannelDetails) }">
                <div
                  class="editable-cell"
                  contenteditable="true"
                  dir="ltr"
                  data-placeholder="Add channel details"
                  v-html="row.CommunicationChannelDetails"
                  @focus="setActiveCell(row, 'CommunicationChannelDetails', $event)"
                  @input="markCellDirty($event)"
                  @blur="saveField(row, 'CommunicationChannelDetails', $event)">
                </div>
              </td>
              <td :class="{ 'note-cell-has-value': fieldHasValue(row.LawyerGroupDetails) }">
                <div
                  class="editable-cell"
                  contenteditable="true"
                  dir="ltr"
                  data-placeholder="Add lawyer group details"
                  v-html="row.LawyerGroupDetails"
                  @focus="setActiveCell(row, 'LawyerGroupDetails', $event)"
                  @input="markCellDirty($event)"
                  @blur="saveField(row, 'LawyerGroupDetails', $event)">
                </div>
              </td>
              <td class="date-column" :class="{ 'note-cell-has-value': !!row.LastCounselUpdate }">
                <input
                  class="input is-small counsel-date-input"
                  type="date"
                  :value="formatDateInput(row.LastCounselUpdate)"
                  @focus="activeCell = null"
                  @blur="saveDateField(row, 'LastCounselUpdate', $event)">
              </td>
              <td class="updated-column">
                <div v-if="row.SaveState == 'saving'" class="save-state has-text-grey">Saving...</div>
                <div v-if="row.SaveState == 'saved'" class="save-state has-text-success">Saved</div>
                <div v-if="row.SaveState == 'error'" class="save-state has-text-danger">Not saved</div>
                <div v-if="row.LastUpdated">{{ row.LastUpdated | moment('M/DD/YYYY') }}</div>
                <small v-if="row.UpdatedByName" class="has-text-grey">{{ row.UpdatedByName }}</small>
              </td>
            </tr>
            <tr v-if="filteredNotes.length == 0">
              <td colspan="7" class="empty-state">
                <span v-if="filter">No countries match the current filter.</span>
                <span v-else>No country rows were returned by the API.</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: "countryIndexNotes",
    data() {
      return {
        activeCell: null,
        error: "",
        filter: "",
        isDirty: false,
        isLoading: false,
        hasConflict: false,
        lastSaved: null,
        notes: []
      };
    },
    computed: {
      filteredNotes() {
        const filter = this.filter.toLowerCase();

        if (!filter) {
          return this.notes;
        }

        return this.notes.filter((note) => note.Country.toLowerCase().indexOf(filter) >= 0);
      }
    },
    methods: {
      async getNotes() {
        this.isLoading = true;
        this.error = "";
        this.hasConflict = false;

        try {
          const response = await this.$http.get("api/countryIndexNote");
          this.notes = response.data.map((note) => this.normalizeNote(note));
          this.isDirty = false;
        } catch (err) {
          console.error(err);
          this.error = "Unable to load country index notes. Confirm the API is running and the CountryIndexNote table migration has been applied.";
        } finally {
          this.isLoading = false;
        }
      },
      async saveNotes() {
        this.commitActiveCell();
        this.error = "";
        this.hasConflict = false;

        try {
          const response = await this.$http.post("api/countryIndexNote", this.notes);
          this.notes = response.data.map((note) => this.normalizeNote(note));
          this.lastSaved = new Date();
          this.isDirty = false;
          this.$buefy.notification.open({
            duration: 3000,
            message: "Country index notes saved.",
            type: "is-success"
          });
        } catch (err) {
          console.error(err);
          if (err.response && err.response.status == 409) {
            this.hasConflict = true;
            this.error = err.response.data && err.response.data.Message
              ? err.response.data.Message
              : "These notes were updated by another user. Reload the page before saving.";
          } else {
            this.error = "Unable to save country index notes. Confirm the CountryIndexNote table migration has been applied.";
          }
        } finally {
        }
      },
      async saveField(row, field, event) {
        const value = this.normalizeHtml(event.target.innerHTML);
        if (!value) {
          event.target.innerHTML = "";
        }

        this.setField(row, field, value);
        this.queueRowSave(row);
      },
      queueRowSave(row) {
        if (!this.hasUnsavedChanges(row)) {
          this.isDirty = this.notes.some((note) => this.hasUnsavedChanges(note));
          return;
        }

        if (row.IsSaving) {
          this.$set(row, "PendingSave", true);
          this.$set(row, "SaveState", "saving");
          return;
        }

        this.saveRow(row);
      },
      async saveRow(row) {
        const payload = this.createSavePayload(row);
        const savedValues = this.originalValues(payload);

        this.$set(row, "IsSaving", true);
        this.$set(row, "PendingSave", false);
        this.$set(row, "SaveState", "saving");
        this.error = "";
        this.hasConflict = false;

        try {
          const response = await this.$http.post(`api/countryIndexNote/${row.CountryId}`, payload);
          this.applySavedRow(row, response.data, savedValues);
          this.lastSaved = new Date();

          this.$set(row, "IsSaving", false);

          if (row.PendingSave || this.hasUnsavedChanges(row)) {
            this.$set(row, "PendingSave", false);
            this.queueRowSave(row);
          } else {
            this.$set(row, "SaveState", "saved");
          }
        } catch (err) {
          console.error(err);
          this.$set(row, "IsSaving", false);
          this.$set(row, "PendingSave", false);
          this.$set(row, "SaveState", "error");

          if (err.response && err.response.status == 409) {
            this.hasConflict = true;
            this.error = err.response.data && err.response.data.Message
              ? err.response.data.Message
              : `${row.Country} was updated by another user. Reload notes before saving this row.`;
          } else {
            this.error = `Unable to save notes for ${row.Country}.`;
          }
        }
      },
      formatCell(command, value) {
        if (!this.activeCell) {
          return;
        }

        document.execCommand(command, false, value);
        this.activeCell.value = this.activeCell.element.innerHTML;
        this.isDirty = true;
        this.activeCell.element.focus();
      },
      setActiveCell(row, field, event) {
        this.activeCell = {
          row,
          field,
          element: event.target,
          value: event.target.innerHTML
        };
      },
      commitActiveCell() {
        if (this.activeCell) {
          this.activeCell.row[this.activeCell.field] = this.activeCell.element.innerHTML;
          this.activeCell.value = this.activeCell.element.innerHTML;
        }
      },
      markCellDirty(event) {
        if (this.activeCell) {
          this.activeCell.value = event.target.innerHTML;
        }

        const cell = event.target.closest("td");
        if (cell && this.fieldHasValue(event.target.innerHTML)) {
          cell.classList.add("note-cell-has-value");
        } else if (cell) {
          cell.classList.remove("note-cell-has-value");
        }

        this.isDirty = true;
      },
      setField(row, field, value) {
        row[field] = this.normalizeHtml(value);
        this.isDirty = true;
      },
      saveDateField(row, field, event) {
        row[field] = this.normalizeDateInput(event.target.value);
        this.isDirty = true;
        this.queueRowSave(row);
      },
      hasUnsavedChanges(row) {
        return row.WatchlistStatus != row.Original.WatchlistStatus
          || row.OtherOpenIssues != row.Original.OtherOpenIssues
          || row.CommunicationChannelDetails != row.Original.CommunicationChannelDetails
          || row.LawyerGroupDetails != row.Original.LawyerGroupDetails
          || this.normalizeDateInput(row.LastCounselUpdate) != row.Original.LastCounselUpdate;
      },
      createSavePayload(row) {
        return {
          Id: row.Id,
          CountryId: row.CountryId,
          Country: row.Country,
          WatchlistStatus: row.WatchlistStatus,
          OtherOpenIssues: row.OtherOpenIssues,
          CommunicationChannelDetails: row.CommunicationChannelDetails,
          LawyerGroupDetails: row.LawyerGroupDetails,
          LastCounselUpdate: this.normalizeDateInput(row.LastCounselUpdate) || null,
          LastUpdated: row.LastUpdated,
          LastUpdatedTicks: row.LastUpdatedTicks,
          UpdatedBy: row.UpdatedBy,
          UpdatedByName: row.UpdatedByName
        };
      },
      applySavedRow(row, savedRow, savedValues) {
        row.Id = savedRow.Id;
        row.LastUpdated = savedRow.LastUpdated;
        row.LastUpdatedTicks = savedRow.LastUpdatedTicks;
        row.UpdatedBy = savedRow.UpdatedBy;
        row.UpdatedByName = savedRow.UpdatedByName;
        row.LastCounselUpdate = this.normalizeDateInput(savedRow.LastCounselUpdate);
        row.Original = savedValues;
        this.isDirty = this.notes.some((note) => this.hasUnsavedChanges(note));
      },
      originalValues(note) {
        return {
          WatchlistStatus: this.normalizeHtml(note.WatchlistStatus),
          OtherOpenIssues: this.normalizeHtml(note.OtherOpenIssues),
          CommunicationChannelDetails: this.normalizeHtml(note.CommunicationChannelDetails),
          LawyerGroupDetails: this.normalizeHtml(note.LawyerGroupDetails),
          LastCounselUpdate: this.normalizeDateInput(note.LastCounselUpdate)
        };
      },
      formatDateInput(value) {
        return this.normalizeDateInput(value);
      },
      normalizeDateInput(value) {
        if (!value) {
          return "";
        }

        if (typeof value == "string") {
          return value.length >= 10 ? value.substring(0, 10) : value;
        }

        if (value instanceof Date && !isNaN(value.getTime())) {
          return value.toISOString().substring(0, 10);
        }

        return "";
      },
      normalizeHtml(value) {
        if (!value) {
          return "";
        }

        const visibleText = value
          .replace(/<br\s*\/?>/gi, "")
          .replace(/&nbsp;/gi, " ")
          .replace(/<[^>]*>/g, "")
          .trim();

        return visibleText ? value : "";
      },
      fieldHasValue(value) {
        return !!this.normalizeHtml(value);
      },
      normalizeNote(note) {
        const normalized = {
          ...note,
          WatchlistStatus: this.normalizeHtml(note.WatchlistStatus),
          OtherOpenIssues: this.normalizeHtml(note.OtherOpenIssues),
          CommunicationChannelDetails: this.normalizeHtml(note.CommunicationChannelDetails),
          LawyerGroupDetails: this.normalizeHtml(note.LawyerGroupDetails),
          LastCounselUpdate: this.normalizeDateInput(note.LastCounselUpdate),
          IsSaving: false,
          PendingSave: false,
          SaveState: ""
        };

        normalized.Original = this.originalValues(normalized);

        return normalized;
      }
    },
    created() {
      this.getNotes();
    }
  };
</script>

<style scoped>
  .page-header {
    margin-bottom: 16px;
  }

  .notes-toolbar {
    align-items: center;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
  }

  .format-controls,
  .table-controls {
    align-items: center;
    display: flex;
    gap: 6px;
  }

  .country-filter {
    width: 220px;
  }

  .notes-table-wrap {
    border: 1px solid #dbdbdb;
    max-height: calc(100vh - 260px);
    min-height: 420px;
    overflow: auto;
    position: relative;
  }

  .notes-table {
    table-layout: fixed;
    margin-bottom: 0;
  }

  .notes-table thead th {
    background: #f7f9fb;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  .notes-table th,
  .notes-table td {
    vertical-align: top;
  }

  .country-column {
    background: #fff;
    left: 0;
    position: sticky;
    width: 190px;
    z-index: 1;
  }

  thead .country-column {
    z-index: 3;
  }

  .updated-column {
    width: 130px;
  }

  .date-column {
    width: 160px;
  }

  .save-state {
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 2px;
  }

  .editable-cell {
    cursor: text;
    direction: ltr;
    min-height: 96px;
    outline: none;
    overflow-wrap: anywhere;
    padding: 6px;
    text-align: left;
  }

  .editable-cell:empty::before {
    color: #8c98a4;
    content: attr(data-placeholder);
  }

  .notes-table td.note-cell-has-value {
    background: #fff8d9;
  }

  .counsel-date-input {
    background: transparent;
  }

  .editable-cell:focus {
    box-shadow: inset 0 0 0 2px #3273dc;
  }

  .editable-cell:focus::before {
    content: "";
  }

  .empty-state {
    color: #7a7a7a;
    padding: 36px;
    text-align: center;
  }
</style>
